import { Button, Card, Descriptions, Spin, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import axios from '../config/axios';

interface MDMHealth {
	provider: string;
	healthy: boolean;
	degraded: boolean;
	checked_at: string;
	ping: { ok: boolean; reason: string; http_status: number | null; detail: string };
	token: {
		present: boolean;
		malformed: boolean;
		expires_at: string | null;
		expires_display: string | null;
		days_remaining: number | null;
		expired: boolean;
		expiring_soon: boolean;
	};
	problems: string[];
	active_contracts: number;
}

// Every lock date and unlock rides on this one credential, and the calls that use
// it report nothing when they fail, so its state is worth showing where the rest
// of the system's settings are.
export default function MDMHealthCard() {
	const { data, isLoading, isFetching, refetch } = useQuery<MDMHealth>({
		queryKey: ['mdm-health'],
		queryFn: async () => {
			const res = await axios.get('/admin/super/devices/mdm/health', {
				withCredentials: true,
			});
			return res.data.results;
		},
		refetchOnWindowFocus: false,
	});

	const status = !data
		? { color: 'default', label: '—' }
		: !data.healthy
			? { color: 'error', label: 'Down' }
			: data.problems.length
				? { color: 'warning', label: 'Expiring' }
				: { color: 'success', label: 'Connected' };

	const expiry = data?.token.expires_at;
	const days = data?.token.days_remaining;

	return (
		<Card
			className="mb-4 shadow-sm"
			title={
				<span>
					MDM <Tag color={status.color}>{status.label}</Tag>
				</span>
			}
			extra={
				<Button
					size="small"
					icon={<ReloadOutlined />}
					loading={isFetching}
					onClick={() => refetch()}
				/>
			}
		>
			{isLoading ? (
				<div className="flex justify-center py-6">
					<Spin />
				</div>
			) : !data ? null : (
				<>
					<Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
						<Descriptions.Item label="Token expires">
							{expiry ? (
								<span className={data.token.expired ? 'text-red-600' : undefined}>
									{dayjs(expiry).format('DD MMM YYYY')}
									{/* Past expiry the day count goes negative, which reads as a
									    puzzle rather than a state. Say what happened instead. */}
									{data.token.expired
										? days != null && ` — expired ${Math.abs(days)}d ago`
										: days != null && ` (${days}d)`}
								</span>
							) : data.token.present ? (
								'unreadable'
							) : (
								'not set'
							)}
						</Descriptions.Item>
						<Descriptions.Item label="Ping">
							{data.ping.ok
								? 'ok'
								: `${data.ping.reason}${
										data.ping.http_status ? ` (${data.ping.http_status})` : ''
									}`}
						</Descriptions.Item>
						<Descriptions.Item label="Active contracts">
							{data.active_contracts}
						</Descriptions.Item>
						<Descriptions.Item label="Checked">
							{dayjs(data.checked_at).format('DD MMM HH:mm')}
						</Descriptions.Item>
					</Descriptions>

					{data.problems.length > 0 && (
						<ul className="mt-2 mb-0 list-disc pl-5 text-sm text-red-600">
							{data.problems.map((p) => (
								<li key={p}>{p}</li>
							))}
						</ul>
					)}
				</>
			)}
		</Card>
	);
}
